import { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { updateCoil } from "@/lib/api";
import { toast } from "sonner";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { fetchCoils } from "@/lib/api";
import { Save, Loader2 } from "lucide-react";

const GRADES = [
  "50C470", "50C800", "50C1000", "DC01", "DC03", "DC04",
  "AISI 304", "AISI 316", "AISI 410", "AISI 430",
  "HR (Hot Rolled)", "CR (Cold Rolled)", "GI (Galvanized)",
];

const Row = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold tracking-wide uppercase text-slate-600">
      {label}
    </Label>
    {children}
  </div>
);

const ReadOnly = ({ label, value, testId }) => (
  <div className="space-y-1">
    <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{label}</p>
    <p data-testid={testId} className="font-mono-data text-sm text-slate-900">{value || "—"}</p>
  </div>
);

const EditSFGDialog = ({ coil, open, onOpenChange }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  // Resolve the mother coil's supplier from the cached coils list so we can
  // show "Mother Coil Supplier Name" without a dedicated endpoint.
  const { data: allCoils = [] } = useQuery({
    queryKey: ["coils"],
    queryFn: () => fetchCoils(),
    staleTime: 60_000,
  });
  const motherSupplier = useMemo(() => {
    if (!coil?.mother_coil_id) return "";
    const m = allCoils.find((c) => c.coil_id === coil.mother_coil_id);
    return m?.supplier || "";
  }, [allCoils, coil?.mother_coil_id]);

  useEffect(() => {
    if (coil) {
      setForm({
        grade: coil.grade || "",
        slit_width_mm: coil.slit_width_mm ?? coil.width_mm ?? "",
        thickness_mm: coil.thickness_mm ?? "",
        weight_kg: coil.weight_kg ?? "",
        notes: coil.notes || "",
        sfg_count: coil.sfg_count ?? 1,
      });
    }
  }, [coil]);

  const gradeOptions = useMemo(
    () => [form?.grade, ...GRADES.filter((g) => g !== form?.grade)].filter(Boolean),
    [form?.grade],
  );

  if (!form) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const countOk = /^\d+$/.test(String(form.sfg_count)) && Number(form.sfg_count) >= 1 && Number(form.sfg_count) <= 999;
  const dirty = (
    form.grade !== (coil.grade || "")
    || Number(form.slit_width_mm) !== Number(coil.slit_width_mm ?? coil.width_mm)
    || Number(form.thickness_mm) !== Number(coil.thickness_mm)
    || Number(form.weight_kg) !== Number(coil.weight_kg)
    || form.notes !== (coil.notes || "")
    || Number(form.sfg_count) !== Number(coil.sfg_count ?? 1)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {
        grade: form.grade,
        slit_width_mm: Number(form.slit_width_mm),
        thickness_mm: Number(form.thickness_mm),
        weight_kg: Number(form.weight_kg),
        notes: form.notes,
        ...(Number(form.sfg_count) !== Number(coil.sfg_count ?? 1) && { sfg_count: Number(form.sfg_count) }),
      };
      await updateCoil(coil.id, patch);
      toast.success(`SFG ${coil.coil_id} updated`);
      queryClient.invalidateQueries({ queryKey: ["coils"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update SFG");
    } finally {
      setSaving(false);
    }
  };

  const createdDisplay = coil.created_at ? String(coil.created_at).slice(0, 10) : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-sfg-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Edit SFG Coil <span className="font-mono-data text-slate-700">{coil.coil_id}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Read-only reference block */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <ReadOnly label="SFG ID" value={coil.coil_id} testId="ro-sfg-id" />
          <ReadOnly label="Mother Coil ID" value={coil.mother_coil_id} testId="ro-mother-coil-id" />
          <ReadOnly label="Mother Coil Supplier" value={motherSupplier} testId="ro-mother-supplier" />
          <ReadOnly label="Batch ID" value={coil.sfg_batch_id} testId="ro-batch-id" />
          <ReadOnly label="Created" value={createdDisplay} testId="ro-created" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <Row id="sfg-edit-grade" label="Grade">
            <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
              <SelectTrigger data-testid="sfg-edit-grade"><SelectValue /></SelectTrigger>
              <SelectContent>
                {gradeOptions.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>
          <Row id="sfg-edit-slit-width" label="Slit Width (mm)">
            <Input id="sfg-edit-slit-width" data-testid="sfg-edit-slit-width" type="number" step="0.1" value={form.slit_width_mm} onChange={(e) => set("slit_width_mm", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="sfg-edit-thickness" label="Thickness (mm)">
            <Input id="sfg-edit-thickness" data-testid="sfg-edit-thickness" type="number" step="0.01" value={form.thickness_mm} onChange={(e) => set("thickness_mm", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="sfg-edit-weight" label="Net Weight (kg)">
            <Input id="sfg-edit-weight" data-testid="sfg-edit-weight" type="number" step="0.01" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="font-mono-data" />
          </Row>
          <Row id="sfg-edit-count" label="No of SFGs">
            <Input id="sfg-edit-count" data-testid="sfg-edit-count" type="number" min="1" max="999" step="1" value={form.sfg_count} onChange={(e) => set("sfg_count", e.target.value)} className={`font-mono-data ${countOk ? "" : "border-red-500"}`} />
            {!countOk && <p data-testid="sfg-edit-count-error" className="text-xs text-red-600 mt-1">Enter a whole number from 1 to 999</p>}
          </Row>
          <div className="sm:col-span-2">
            <Row id="sfg-edit-notes" label="Notes">
              <Textarea id="sfg-edit-notes" data-testid="sfg-edit-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </Row>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-sfg-edit-btn" className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty || !countOk}
            data-testid="save-sfg-edit-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save changes</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditSFGDialog;
